import { useEffect, useMemo, useState } from "react";
import { useNavigate, useLocation, useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ArrowLeft, ShoppingBag, Upload, Check, ChevronLeft, ChevronRight, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import SavePortraitCTA from "../components/SavePortraitCTA";
import { useAuth } from "../contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// localStorage keys for the "lose this generation" warning. We track how
// many times the anonymous customer has SEEN the modal so we can suppress
// the "Don't show this again" option until the second viewing (per spec —
// don't nag a customer who clearly doesn't want to log in).
const WARN_COUNT_KEY = "tsp_lose_warning_count";
const WARN_SUPPRESS_KEY = "tsp_lose_warning_suppress";

/**
 * Small inline carousel for variant cards. Customers cycle through the
 * different mockup angles (e.g. mug front / mug side / framed wall view)
 * that Nano Banana composed for the same artwork. No autoplay — taps only.
 */
const VariantCarousel = ({ urls, alt, className, testId }) => {
  const [idx, setIdx] = useState(0);
  const safe = Array.isArray(urls) && urls.length > 0 ? urls : [];
  if (safe.length === 0) return null;
  const goPrev = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIdx((i) => (i - 1 + safe.length) % safe.length);
  };
  const goNext = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIdx((i) => (i + 1) % safe.length);
  };
  return (
    <div className={`relative ${className || ""}`} data-testid={testId}>
      <img
        src={safe[idx]}
        alt={alt}
        className="w-full h-full object-contain"
      />
      {safe.length > 1 && (
        <>
          <button
            type="button"
            onClick={goPrev}
            className="absolute left-1 top-1/2 -translate-y-1/2 bg-white/85 hover:bg-white p-1 rounded-full shadow"
            aria-label="Previous mockup"
            data-testid={`${testId}-prev`}
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={goNext}
            className="absolute right-1 top-1/2 -translate-y-1/2 bg-white/85 hover:bg-white p-1 rounded-full shadow"
            aria-label="Next mockup"
            data-testid={`${testId}-next`}
          >
            <ChevronRight className="h-4 w-4" />
          </button>
          <div className="absolute bottom-1 left-1/2 -translate-x-1/2 flex gap-1">
            {safe.map((_, i) => (
              <span
                key={i}
                className={`h-1.5 w-1.5 rounded-full ${
                  i === idx ? "bg-[#C8A27A]" : "bg-white/70 border border-[#C8A27A]/40"
                }`}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
};

const StyleSelectionPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();

  // Warning modal for anonymous customers: shown when they try to upload
  // another image (which would discard the current generation). Spec:
  //  - 1st modal open → no "Don't show this again" option.
  //  - 2nd+ modal open → checkbox is offered. Checking it persists across
  //    sessions so the customer is never nagged again.
  const [warnOpen, setWarnOpen] = useState(false);
  const [warnCount, setWarnCount] = useState(0);
  const [dontShow, setDontShow] = useState(false);
  const showDontShowOption = warnCount >= 2;

  // Either we arrived from the upload flow (state populated) OR the user
  // clicked a saved portrait from /account (only ?job_id=X in the URL).
  // In the second case we hydrate from the public job endpoint so they
  // can pick a variant and check out without re-uploading.
  const [hydrated, setHydrated] = useState(location.state || null);
  const [hydrating, setHydrating] = useState(false);

  const urlJobId = searchParams.get("job_id");

  useEffect(() => {
    // Already have state → nothing to do.
    if (hydrated) return;
    // No state AND no job_id → bounce to upload (existing behavior).
    if (!urlJobId) {
      toast.error("No artwork available. Please upload an image first.");
      navigate("/upload");
      return;
    }
    setHydrating(true);
    axios
      .get(`${API}/generate-styles/${urlJobId}`)
      .then((res) => {
        const job = res.data || {};
        const styles = Array.isArray(job.styles) ? job.styles : [];
        if (styles.length === 0) {
          toast.error("This portrait is no longer available.");
          navigate("/upload");
          return;
        }
        setHydrated({
          originalImage: job.original_url || "",
          styles,
          petDescription: job.pet_description || "",
          petName: job.pet_name || "",
          category: job.category || null,
          styleId: job.style_id || null,
          selectedSize: job.size_id || "a4",
          couponCode: null,
          jobId: urlJobId,
        });
      })
      .catch(() => {
        toast.error("Could not load this portrait.");
        navigate("/upload");
      })
      .finally(() => setHydrating(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlJobId]);

  const { originalImage, styles, petDescription, petName, category, styleId, selectedSize, couponCode, jobId } = hydrated || {};
  const validStyles = useMemo(
    () => (Array.isArray(styles) ? styles.filter((s) => s && s.image_url) : []),
    [styles]
  );
  const [selectedId, setSelectedId] = useState(null);
  useEffect(() => {
    // Sync the default selection once styles arrive (covers both the
    // upload-flow synchronous path and the async hydrate-from-job_id path).
    if (!selectedId && validStyles.length > 0) {
      setSelectedId(validStyles[0].style_id);
    }
  }, [validStyles, selectedId]);
  const selected = validStyles.find((s) => s.style_id === selectedId) || validStyles[0];
  const isMulti = validStyles.length > 1;

  // Build the upload URL that preserves the current category + style + size so
  // "Try with another Photo" and "Back" land on the right upload variant
  // (e.g. RCB → /upload?category=ipl&style=ipl_rcb, not the generic Modern flow).
  const buildUploadUrl = () => {
    const params = new URLSearchParams();
    if (category) params.set("category", category);
    if (styleId) params.set("style", styleId);
    if (selectedSize) params.set("size", selectedSize);
    if (couponCode) params.set("coupon", couponCode);
    const qs = params.toString();
    return qs ? `/upload?${qs}` : "/upload";
  };

  const handleContinue = () => {
    if (!selected) return;
    navigate("/checkout", {
      state: {
        originalImage,
        selectedStyle: selected,
        styles: validStyles,
        petDescription,
        petName,
        selectedSize: selectedSize || "8x10",
        couponCode: couponCode || null,
      },
    });
  };

  const handleUploadAnother = () => {
    // Logged-in customers don't lose anything — the job is bound to their
    // account already. Opted-out anonymous customers (Don't show again)
    // also skip the warning. Everyone else sees the modal first.
    let suppress = false;
    try {
      suppress = localStorage.getItem(WARN_SUPPRESS_KEY) === "1";
    } catch {
      /* ignore */
    }
    if (user || suppress) {
      navigate(buildUploadUrl());
      return;
    }
    let prev = 0;
    try {
      prev = parseInt(localStorage.getItem(WARN_COUNT_KEY) || "0", 10) || 0;
    } catch {
      /* ignore */
    }
    const next = prev + 1;
    try {
      localStorage.setItem(WARN_COUNT_KEY, String(next));
    } catch {
      /* ignore */
    }
    setWarnCount(next);
    setDontShow(false);
    setWarnOpen(true);
  };

  const confirmLoseAndContinue = () => {
    // Persist the suppress flag if the customer ticked the box AND we
    // actually showed it (i.e. this was at least the second exposure).
    if (showDontShowOption && dontShow) {
      try {
        localStorage.setItem(WARN_SUPPRESS_KEY, "1");
      } catch {
        /* ignore */
      }
    }
    setWarnOpen(false);
    navigate(buildUploadUrl());
  };

  const dismissWarningAndSave = () => {
    // Close the modal and scroll to the inline SavePortraitCTA card so the
    // customer can sign up without losing the current generation.
    setWarnOpen(false);
    setTimeout(() => {
      const el = document.querySelector('[data-testid="save-portrait-anon"], [data-testid="save-portrait-authed"]');
      if (el && typeof el.scrollIntoView === "function") {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }, 100);
  };

  if (hydrating) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <p className="text-sm text-[#6B6B6B]" data-testid="styles-hydrating">
          Loading your portrait…
        </p>
      </div>
    );
  }

  if (validStyles.length === 0) return null;

  return (
    <div className="min-h-screen bg-background">
      <header className="glass-header py-5">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 flex items-center justify-between">
          <Button
            variant="ghost"
            onClick={() => navigate(buildUploadUrl())}
            data-testid="back-to-upload-button"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back
          </Button>
          <Link
            to="/"
            className="serif text-2xl tracking-tight text-[#1A1A1A] font-semibold"
          >
            theSnugglePrints
          </Link>
          <div className="w-20"></div>
        </div>
      </header>

      <main className="pt-24 pb-16">
        <div className="max-w-5xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-12">
            <p className="label-uppercase mb-4">
              {isMulti ? "Pick your favourite" : "Your pet portrait"}
            </p>
            <h2 className="serif text-4xl sm:text-5xl text-[#1A1A1A] mb-4">
              {isMulti
                ? `We crafted ${validStyles.length} portraits${petName ? ` for ${petName}` : ""}.`
                : `Here's the portrait we crafted${petName ? ` for ${petName}` : ""}.`}
            </h2>
            <p className="text-[#6B6B6B] max-w-xl mx-auto">
              {isMulti
                ? "Pick the one you love most. We'll print, frame and deliver it to your door."
                : "Like it? Let's get it printed, framed, and delivered to you."}
            </p>
          </div>

          {isMulti ? (
            <div
              className={`grid gap-6 mx-auto ${
                validStyles.length === 2
                  ? "grid-cols-1 sm:grid-cols-2 max-w-3xl"
                  : validStyles.length === 3
                  ? "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 max-w-5xl"
                  : "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 max-w-6xl"
              }`}
            >
              {validStyles.map((s) => {
                const active = s.style_id === selectedId;
                return (
                  <button
                    key={s.style_id}
                    type="button"
                    onClick={() => setSelectedId(s.style_id)}
                    className={`group bg-card border-2 p-3 text-left transition-all relative ${
                      active
                        ? "border-[#C8A27A] shadow-lg"
                        : "border-[#EAE5DF] hover:border-[#D9C7B0]"
                    }`}
                    data-testid={`style-option-${s.style_id}`}
                  >
                    {active && (
                      <span className="absolute top-3 right-3 bg-[#C8A27A] text-white rounded-full p-1.5 z-10">
                        <Check className="h-4 w-4" />
                      </span>
                    )}
                    <div className="bg-white overflow-hidden flex items-center justify-center aspect-[3/4]">
                      <VariantCarousel
                        urls={(s.preview_urls && s.preview_urls.length > 0) ? s.preview_urls : [s.preview_url || s.image_url]}
                        alt={s.style_name}
                        className="w-full h-full"
                        testId={`variant-carousel-${s.style_id}`}
                      />
                    </div>
                    <div className="mt-3 px-1">
                      <h3 className="serif text-lg text-[#1A1A1A]">
                        {s.style_name}
                      </h3>
                    </div>
                  </button>
                );
              })}
            </div>
          ) : (
            <div
              className="bg-card border border-[#EAE5DF] p-4 sm:p-6 mx-auto max-w-xl"
              data-testid="preview-card"
            >
              <div className="w-full overflow-hidden bg-white flex items-center justify-center">
                <VariantCarousel
                  urls={(selected.preview_urls && selected.preview_urls.length > 0) ? selected.preview_urls : [selected.preview_url || selected.image_url]}
                  alt="Your custom pet portrait"
                  className="w-full"
                  testId="preview-image"
                />
              </div>
              <div className="mt-6 text-center">
                <h3 className="serif text-2xl text-[#1A1A1A] mb-1">
                  {selected.style_name || "Custom Pet Portrait"}
                </h3>
                <p className="text-sm text-[#6B6B6B]">
                  Framed preview · printed on premium A4 artboard
                </p>
              </div>
            </div>
          )}

          <div className="flex flex-col sm:flex-row gap-4 max-w-xl mx-auto mt-10">
            <Button
              variant="outline"
              onClick={handleUploadAnother}
              className="flex-1 h-14 text-base"
              data-testid="regenerate-button"
            >
              <Upload className="mr-2 h-5 w-5" />
              Try with another Photo
            </Button>
            <Button
              onClick={handleContinue}
              disabled={!selected}
              className="flex-1 h-14 text-base"
              data-testid="proceed-checkout-button"
            >
              <ShoppingBag className="mr-2 h-5 w-5" />
              Proceed to Checkout
            </Button>
          </div>

          <SavePortraitCTA jobId={jobId} petName={petName} />
        </div>
      </main>

      <Dialog open={warnOpen} onOpenChange={setWarnOpen}>
        <DialogContent className="sm:max-w-md" data-testid="lose-generation-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 serif text-xl text-[#1A1A1A]">
              <AlertTriangle className="h-5 w-5 text-[#C8A27A]" />
              You&apos;ll lose this portrait
            </DialogTitle>
            <DialogDescription className="text-sm text-[#6B6B6B] pt-2">
              Uploading a new photo will discard the AI portrait you just
              generated. Log in or sign up to save it to your account first —
              you can always come back and order it later.
            </DialogDescription>
          </DialogHeader>

          {showDontShowOption && (
            <label
              className="flex items-center gap-2 text-sm text-[#6B6B6B] cursor-pointer select-none mt-2"
              data-testid="lose-generation-dont-show-label"
            >
              <input
                type="checkbox"
                checked={dontShow}
                onChange={(e) => setDontShow(e.target.checked)}
                className="h-4 w-4 accent-[#C8A27A]"
                data-testid="lose-generation-dont-show"
              />
              Don&apos;t show this again
            </label>
          )}

          <DialogFooter className="gap-2 sm:gap-2 flex-col sm:flex-row pt-2">
            <Button
              variant="outline"
              onClick={confirmLoseAndContinue}
              className="sm:flex-1"
              data-testid="lose-generation-continue"
            >
              Continue, lose it
            </Button>
            <Button
              onClick={dismissWarningAndSave}
              className="sm:flex-1"
              data-testid="lose-generation-save"
            >
              Save it first
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default StyleSelectionPage;
